import { useEffect, useRef, useState } from "react";
import { rmsLevelFromBytes, smoothLevel } from "../lib/voiceLevel";

function prefersReducedMotion() {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) { return false; }
}

/**
 * H13 (Ticket 4b-2): nivel de voz REAL del CLIENTE a partir de la pista remota de SIP.js (MediaStream). Crea su
 * propio AudioContext/AnalyserNode SOLO mientras hay `stream` y los cierra SIEMPRE (desmontar, cambiar de
 * stream, colgar) -- nunca deja un AudioContext vivo de más. `prefers-reduced-motion`: en vez de
 * requestAnimationFrame (~30 fps) usa un muestreo espaciado (sigue siendo el nivel real, solo que no "fluye").
 */
export function useRemoteAudioLevel(stream) {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!stream || !AC) { setLevel(0); return undefined; }
    let ctx, analyser, source;
    try {
      ctx = new AC();
      analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);
    } catch (e) { setLevel(0); return undefined; }

    const bytes = new Uint8Array(analyser.frequencyBinCount);
    let prev = 0, lastT = (typeof performance !== "undefined" ? performance.now() : Date.now());
    let raf = null, intervalId = null, alive = true;

    const sample = () => {
      analyser.getByteTimeDomainData(bytes);
      const now = (typeof performance !== "undefined" ? performance.now() : Date.now());
      const dt = Math.max(1 / 60, (now - lastT) / 1000);
      lastT = now;
      prev = smoothLevel(prev, rmsLevelFromBytes(bytes), dt);
      setLevel(prev);
    };

    if (prefersReducedMotion()) {
      intervalId = setInterval(sample, 400);
    } else {
      const loop = () => { if (!alive) return; sample(); raf = requestAnimationFrame(loop); };
      raf = requestAnimationFrame(loop);
    }
    return () => {
      alive = false;
      if (raf) cancelAnimationFrame(raf);
      if (intervalId) clearInterval(intervalId);
      try { source.disconnect(); } catch (e) { /* ya desconectado */ }
      try { analyser.disconnect(); } catch (e) { /* ya desconectado */ }
      try { ctx.close(); } catch (e) { /* ya cerrado */ }
    };
  }, [stream]);
  return level;
}

/**
 * H13: para quien NO tiene audio real (bilingüe solo lectura, admin): nivel "discreto" a partir de eventos ya
 * disponibles (p. ej. "llegó un segmento nuevo del cliente" en la transcripción). Sube de golpe mientras
 * `active` sea true y baja a 0 sola tras `holdMs` sin actividad nueva -- así las barras se ven vivas mientras
 * habla y en reposo cuando calla, sin inventar audio que no se tiene.
 */
export function useDiscreteVoiceLevel(active, holdMs = 1600) {
  const [level, setLevel] = useState(0);
  const timer = useRef(null);
  useEffect(() => {
    if (!active) return undefined;
    setLevel(0.75);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setLevel(0), holdMs);
    return () => clearTimeout(timer.current);
  }, [active, holdMs]);
  return level;
}
